import React, { type ComponentProps, useCallback } from 'react';
import { type Href, useLocalSearchParams, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import { BodyText, Button, DisplayText, theme } from '@sg/garnish';

import { useClickToClaimMachine } from '@order/features-loyalty';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEventEffect } from '@order/Telemetry';

import { LoadingAnimation, NotFoundView, SignedOutViewContent } from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useNavigateBack } from '../../navigation';
import {
  APPLY_REWARD_FADE_IN_DURATION,
  CLAIMED_REWARD_DESTINATION,
  DEFAULT_CHANNEL_SOURCE,
} from './ApplyRewardScreen.constants';
import { ApplyRewardModal } from './components';
import { useClaimStatusMessages } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ApplyRewardScreen = () => {
  const isClickToClaimEnabled = useFeatureFlag('CELS-5669-order-click-to-claim-fe');

  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  const { offerId, utm_source } = useLocalSearchParams<RouteParams>();

  const utmSource = normalizeUtmSource(utm_source);
  const channelSource = utmSource ?? DEFAULT_CHANNEL_SOURCE;

  // ─── Telemetry ───────────────────────────────────────────────────────

  useTrackEventEffect({
    name: 'click-to-claim.opened',
    payload: { offerId: offerId ?? '', channelSource, isLoggedIn },
    skip: !isClickToClaimEnabled || !offerId || isLoggingIn,
  });

  // ─── Gates ───────────────────────────────────────────────────────────

  if (!isClickToClaimEnabled || !offerId) {
    return (
      <ApplyRewardModal>
        <NotFoundView withoutFooter />
      </ApplyRewardModal>
    );
  }

  if (isLoggingIn) {
    return (
      <ApplyRewardModal>
        <LoadingAnimation />
      </ApplyRewardModal>
    );
  }

  if (!isLoggedIn) {
    return (
      <ApplyRewardModal palette="cream">
        <SignedOutViewContent redirectTo={buildRedirectTarget(offerId, utmSource)} />
      </ApplyRewardModal>
    );
  }

  return <ApplyRewardScreenReady offerId={offerId} channelSource={channelSource} />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ApplyRewardScreenReady = (props: ApplyRewardScreenReadyProps) => {
  const { offerId, channelSource } = props;

  const router = useRouter();
  const navigateBack = useNavigateBack();
  const { track } = useTelemetry();

  const { status, reward } = useClickToClaimMachine(
    { offerId, channelSource },
    {
      actions: {
        onClaimSucceeded: ({ context }) => {
          track('click-to-claim.claim.succeeded', {
            offerId: context.offerId,
            channelSource: context.channelSource,
            rewardId: context.reward?.id,
          });
        },
        onClaimFailed: ({ context }, params) => {
          track('click-to-claim.claim.failed', {
            offerId: context.offerId,
            channelSource: context.channelSource,
            failureReason: params.failureReason,
          });
        },
      },
    },
  );

  const { header, body, cta } = useClaimStatusMessages({ status, rewardName: reward?.name });

  const isClaiming = status === 'claiming';
  const isClaimed = status === 'claimed';

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleContinue = useCallback(() => {
    track('click-to-claim.continue.cta_tapped', { offerId, channelSource, claimStatus: status });

    if (isClaimed) {
      router.dismissTo(CLAIMED_REWARD_DESTINATION);

      return;
    }

    navigateBack();
  }, [channelSource, isClaimed, navigateBack, offerId, router, status, track]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ApplyRewardModal>
      <Animated.View
        key={status}
        entering={FadeIn.duration(APPLY_REWARD_FADE_IN_DURATION)}
        style={styles.textContainer}
      >
        <DisplayText sizeMatch={HEADER_SIZE_MATCH} style={styles.headerText}>
          {header}
        </DisplayText>

        <BodyText sizeMatch={BODY_SIZE_MATCH} style={styles.bodyText}>
          {body}
        </BodyText>
      </Animated.View>

      <Button
        size="large-wide"
        style={styles.button}
        palette="kaleLightest"
        accessibilityLabel={cta}
        isLoading={isClaiming}
        onPress={handleContinue}
      >
        <BodyText sizeMatch={BODY_SIZE_MATCH} style={styles.buttonText}>
          {cta}
        </BodyText>
      </Button>
    </ApplyRewardModal>
  );
};

// ─── Pure Functions ──────────────────────────────────────────────────────────

const normalizeUtmSource = (utmSource: RouteParams['utm_source']): string | undefined => {
  const value = Array.isArray(utmSource) ? utmSource[0] : utmSource;

  if (!value) {
    return;
  }

  return value.trim().toLowerCase();
};

const buildRedirectTarget = (offerId: string, utmSource: string | undefined): Href => {
  const encodedOfferId = encodeURIComponent(offerId);

  return utmSource
    ? `/apply-reward/${encodedOfferId}?utm_source=${encodeURIComponent(utmSource)}`
    : `/apply-reward/${encodedOfferId}`;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const HEADER_SIZE_MATCH: ComponentProps<typeof DisplayText>['sizeMatch'] = ['32', '40'];
const BODY_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['16', '18'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  textContainer: {
    gap: theme.spacing['6'],
  },
  headerText: {
    color: theme.colors.SWEET_CORN,
    textAlign: 'center',
  },
  bodyText: {
    color: theme.colors.WHITE,
    textAlign: 'center',
  },
  button: {
    backgroundColor: theme.colors.SWEET_CORN,
  },
  buttonText: {
    color: theme.colors.DARK_KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  offerId?: string;
  utm_source?: string | string[];
};

type ApplyRewardScreenReadyProps = {
  offerId: string;
  channelSource: string;
};
